/**
 * 확정본 수정이 배분에 주는 영향. 재확정하면 서버가 같은 규칙으로 배분을 정리한다.
 * 컨테이너가 1개면 바뀐 수량을 그대로 따라가고, 2개 이상이면 배분이 여전히 성립하는 라인은 유지하고 성립하지 않는 라인만 미배분으로 돌린다.
 * 컨테이너별 수량을 나눠 줄이지 않는다.
 */
export const LINE_IMPACT = {
    KEEP: 'keep',
    AUTO: 'auto',
    UNALLOCATE: 'unallocate',
    DELETE: 'delete',
    NEW_UNALLOCATED: 'new_unallocated',
};

/** 화주가 라인에 덧붙인 데이터(커스텀 값·별표). 화주 화면 테이블이 생기기 전이라 지워질 건수는 늘 0이다. */
const SHIPPER_DATA_COUNT = 0;

const num = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};
const unit = (value) => String(value ?? '').trim().toUpperCase();
const fmt = (value) => num(value).toLocaleString();

/** 확정 품목에 실린 몫. 수량 0 이하는 실린 것이 아니다. */
const allocatedEntries = (allocations, itemId) => (allocations[itemId] ?? []).filter((a) => num(a.alloc_qty) > 0);

/**
 * 수정 라인 하나의 배분 영향. item은 그 라인이 고치는 확정 품목(새 라인이면 null)이다.
 * 바뀐 것이 없거나 배분과 무관한 라인은 null을 돌려준다.
 */
export function lineImpact(line, item, allocations, containerCount) {
    if (containerCount === 0) return null;
    if (!item) {
        if (containerCount === 1) return { kind: LINE_IMPACT.AUTO, message: 'New line. It will be allocated to the single container automatically.' };
        return { kind: LINE_IMPACT.NEW_UNALLOCATED, message: 'New line. It will start unallocated.' };
    }
    const allocated = allocatedEntries(allocations, item.id).reduce((sum, a) => sum + num(a.alloc_qty), 0);
    const unitChanged = unit(line.qty_uom) !== unit(item.qty_uom);
    const qtyChanged = num(line.qty) !== num(item.qty);
    if (containerCount === 1) {
        if (unitChanged || qtyChanged) return { kind: LINE_IMPACT.AUTO, message: `The single container's allocation will follow the new quantity (${`${fmt(line.qty)} ${line.qty_uom || ''}`.trim()}).` };
        return allocated > 0 ? { kind: LINE_IMPACT.KEEP, message: null } : null;
    }
    if (allocated <= 0) return null;
    if (unitChanged) return { kind: LINE_IMPACT.UNALLOCATE, message: `Unit changed from ${item.qty_uom || '—'} to ${line.qty_uom || '—'}. Its allocation will be cleared and the line becomes unallocated.` };
    if (num(line.qty) < allocated) return { kind: LINE_IMPACT.UNALLOCATE, message: `Quantity ${fmt(line.qty)} is below the allocated ${fmt(allocated)}. Its allocation will be cleared and the line becomes unallocated.` };
    if (num(line.qty) > num(item.qty)) return { kind: LINE_IMPACT.KEEP, message: `Allocation is kept. The added ${fmt(num(line.qty) - num(item.qty))} stays unallocated.` };
    if (qtyChanged) return { kind: LINE_IMPACT.KEEP, message: 'Allocation is kept.' };
    return { kind: LINE_IMPACT.KEEP, message: null };
}

/**
 * 수정 초안 전체의 배분 영향 요약. items는 이 문서의 확정 품목, allocations는 품목 id → 몫 목록이다.
 * 수정 라인은 item_id로 확정 품목과 짝을 짓고, 짝이 없는 확정 품목은 삭제된 라인이다.
 * byLine은 라인 id → { kind, message, allocated(컨테이너 번호·수량) }로, 배분된 라인 표시와 라인별 안내에 쓴다.
 */
export function summarizeEditImpact({ lines, items, allocations, containers }) {
    const containerCount = containers.length;
    const numberOf = Object.fromEntries(containers.map((c) => [c.id, c.cntr_number]));
    const itemsById = new Map(items.map((i) => [i.id, i]));
    const keptIds = new Set();
    const counts = { kept: 0, auto: 0, unallocated: 0, deleted: 0, added: 0 };
    const byLine = {};

    lines.forEach((line) => {
        const item = line.item_id != null ? itemsById.get(line.item_id) ?? null : null;
        if (item) keptIds.add(item.id);
        const impact = lineImpact(line, item, allocations, containerCount);
        const allocated = item ? allocatedEntries(allocations, item.id).map((a) => ({ number: numberOf[a.container_id] ?? `#${a.container_id}`, qty: num(a.alloc_qty) })) : [];
        if (impact || allocated.length) byLine[line.id] = { kind: impact?.kind ?? null, message: impact?.message ?? null, allocated };
        if (!impact) return;
        if (impact.kind === LINE_IMPACT.KEEP) counts.kept += 1;
        if (impact.kind === LINE_IMPACT.AUTO) counts.auto += 1;
        if (impact.kind === LINE_IMPACT.UNALLOCATE) counts.unallocated += 1;
        if (impact.kind === LINE_IMPACT.NEW_UNALLOCATED) counts.added += 1;
    });
    counts.deleted = items.filter((i) => !keptIds.has(i.id)).length;

    return { ...counts, shipperData: SHIPPER_DATA_COUNT, byLine };
}

/** 수량·단위를 고친 배분 라인에 대한 안내. 바뀌지 않은 라인과 새 라인은 뺀다. */
export const changedAllocationNotes = (lines, byLine) =>
    lines
        .map((line, index) => ({ line, index, impact: byLine[line.id] }))
        .filter(({ impact }) => impact?.message && impact.allocated.length > 0)
        .map(({ line, index, impact }) => ({ lineId: line.id, lineNo: index + 1, description: line.description, kind: impact.kind, message: impact.message }));
